import { Box, Button, Flex, VStack, useToast } from "@chakra-ui/react";
import { extractErrorMessage } from "@equidefi/portals";
import type { DTO } from "@equidefi/shared/types/dto";
import { useMemo, useState } from "react";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router";

import { APPROVED } from "@equidefi/constants/accreditation";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { useCreateInvestment } from "@equidefi/portals/hooks/useInvestments";
import { useSavedProfiles } from "@equidefi/portals/hooks/useSavedProfiles";
import { useGetUserVaults } from "@equidefi/portals/hooks/useVault";
import { offeringIsTypeRegA, vaultDisplayName } from "@equidefi/shared";
import { Heading, Text } from "@equidefi/ui";
import { Icon } from "@equidefi/ui/icon";

import { InvestmentLimitCard } from "@/components/investment/InvestmentLimitCard";
import { useCanReinvest, usePassesSuitabilityCheck, useUserInvestments } from "@/hooks/useInvestments";
import { useWorkflowContext } from "@/pages/workflow/context/WorkflowContext";

type SavedProfile = DTO.SavedProfile;
type Vault = DTO.Vault;
type SavedProfileType = DTO.TAAccountType;

type InvestmentOptionType = "expedited" | "saved-profile" | "scratch";

type CapabilityItem = {
  text: string;
  status: "check" | "warning" | "cross";
};

type InvestmentOption = {
  id: string;
  type: InvestmentOptionType;
  name: string;
  label: string;
  savedProfile?: SavedProfile;
  vault?: Vault;
  badge?: string;
  badgeColor?: string;
  capabilities?: CapabilityItem[];
};

const EXPEDITED_CAPABILITIES: CapabilityItem[] = [
  { text: "You can choose your investment amount", status: "check" },
  { text: "Your accreditation is pre-approved", status: "check" },
  { text: "You cannot make changes to personal information", status: "cross" },
  { text: "You cannot add or remove signers", status: "cross" },
];

const SAVED_PROFILE_CAPABILITIES: CapabilityItem[] = [
  { text: "You can choose your investment amount", status: "check" },
  { text: "You can make changes to personal information", status: "check" },
  { text: "You can add or remove signers", status: "check" },
  { text: "You will have to wait for accreditation review", status: "warning" },
];

const SCRATCH_CAPABILITIES: CapabilityItem[] = [
  { text: "You can choose your investment amount", status: "check" },
  {
    text: "You will have to re-enter your investment information",
    status: "warning",
  },
  {
    text: "You will have to re-upload accreditation documents",
    status: "warning",
  },
];

const WorkflowReinvest = () => {
  useDocumentTitle(["Expedite Your Investment"]);

  const navigate = useNavigate();
  const toast = useToast();
  const { offering, user } = useWorkflowContext();
  const [cookies] = useCookies(["bcode", `ref_${offering?.slug}`]);
  if (!offering) {
    throw new Error("Offering id is required.");
  }

  const createInvestment = useCreateInvestment();

  const { data: vaults, isLoading: isVaultsLoading } = useGetUserVaults(user.id, APPROVED, offering.id, {
    refetchOnMount: "always",
  });
  const { data: savedProfiles = [], isLoading: isSavedProfilesLoading } = useSavedProfiles({
    refetchOnMount: "always",
  });

  const { data: investments, isFetchedAfterMount } = useUserInvestments(
    { offering_id: offering.id, vault_id: "" },
    { refetchOnMount: "always" },
  );

  const canReinvest = useCanReinvest(investments);
  const isSameOffering = Boolean(canReinvest && isFetchedAfterMount);

  // Reg A caps non-accredited investors at 10% of their income/net worth. The expedited option
  // reuses the vault's locked financials, so an investor who has already hit that cap can't
  // legitimately invest more this way — block it up front (saved-profile/scratch flows re-collect
  // financials and are re-validated at the subscription step, so they aren't gated here).
  const passesSuitabilityCheck = usePassesSuitabilityCheck(investments);
  const limitReached = Boolean(offeringIsTypeRegA(offering) && investments?.length && !passesSuitabilityCheck);

  const allOptions = useMemo<InvestmentOption[]>(() => {
    const options: InvestmentOption[] = [];

    vaults?.records?.forEach((vault) => {
      if (vault.status !== APPROVED) return;
      options.push({
        id: `expedited-${vault.id}`,
        type: "expedited",
        // TODO: trimmed here rather than in VaultSerializer — display_name also labels issuer screens that want full names.
        name: vaultDisplayName(vault, { middle: "none" }),
        label: getVaultLabel(vault.ta_account_type),
        vault,
        badge: "FASTEST",
        badgeColor: "#2F55D4",
        capabilities: EXPEDITED_CAPABILITIES,
      });
    });

    savedProfiles.forEach((profile) => {
      options.push({
        id: `saved-profile-${profile.id}`,
        type: "saved-profile",
        name: getSavedProfileDisplayName(profile),
        label: getSavedProfileLabel(profile.ta_account_type),
        savedProfile: profile,
        badge: "SAVED PROFILE",
        badgeColor: "#38A169",
        capabilities: SAVED_PROFILE_CAPABILITIES,
      });
    });

    options.push({
      id: "scratch",
      type: "scratch",
      name: "Start A New Investment",
      label: "Start a new investment from scratch",
      capabilities: SCRATCH_CAPABILITIES,
    });

    return options;
  }, [isSameOffering, vaults, savedProfiles]);

  const [selectedOption, setSelectedOption] = useState<InvestmentOption | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const handleCardClick = (option: InvestmentOption) => {
    setSelectedOption(option);
    setExpandedId((prev) => (prev === option.id ? null : option.id));
  };

  const onContinue = async () => {
    if (!selectedOption || !offering?.id) return;

    try {
      if (selectedOption.type === "scratch") {
        const response = await createInvestment.mutateAsync({
          offering_id: offering.id,
          bcode: cookies.bcode,
          ref: cookies[`ref_${offering.slug}`],
        });
        navigate(`/offerings/${offering.slug}/${response.id}/profile`);
        return;
      }

      if (selectedOption.type === "expedited" && selectedOption.vault) {
        const response = await createInvestment.mutateAsync({
          offering_id: offering.id,
          vault_id: selectedOption.vault.id,
          bcode: cookies.bcode,
          ref: cookies[`ref_${offering.slug}`],
        });
        navigate(`/offerings/${offering.slug}/${response.id}/subscription`);
        return;
      }

      if (selectedOption.type === "saved-profile" && selectedOption.savedProfile) {
        const response = await createInvestment.mutateAsync({
          offering_id: offering.id,
          saved_profile_id: selectedOption.savedProfile.id,
          bcode: cookies.bcode,
          ref: cookies[`ref_${offering.slug}`],
        });
        navigate(`/offerings/${offering.slug}/${response.id}/subscription`);
        return;
      }
    } catch (error) {
      toast({
        status: "error",
        description: extractErrorMessage(error, "We couldn't start your investment. Please try again."),
      });
    }
  };

  return (
    <Box px="6" py="5" maxW="528px" mx="auto">
      <VStack align="stretch" spacing="6">
        <Box>
          <Heading fontSize="24px" lineHeight="28px" fontWeight="700" mb="3">
            Expedite Your Investment
          </Heading>

          <Text m="0" fontSize="17px" lineHeight="26px">
            Invest faster by reusing your saved information.
          </Text>
        </Box>

        <VStack align="stretch" spacing="3">
          {allOptions.map((option) => (
            <InvestmentOptionCard
              key={option.id}
              option={option}
              isSelected={selectedOption?.id === option.id}
              isExpanded={expandedId === option.id}
              onClick={() => handleCardClick(option)}
            />
          ))}
        </VStack>

        {limitReached && <InvestmentLimitCard />}

        <Button
          h="14"
          w="full"
          fontSize="18px"
          lineHeight="18px"
          fontWeight="600"
          borderRadius="12px"
          isLoading={createInvestment.isLoading || isSavedProfilesLoading || isVaultsLoading}
          isDisabled={!selectedOption || (limitReached && selectedOption?.type === "expedited")}
          onClick={onContinue}>
          Continue
        </Button>
      </VStack>
    </Box>
  );
};

const CapabilityRow = ({ item }: { item: CapabilityItem }) => {
  const color = item.status === "check" ? "#38A169" : item.status === "warning" ? "#D97706" : "#E53E3E";

  const IconComponent = item.status === "check" ? Icon.Check : item.status === "warning" ? Icon.AlertTriangle : Icon.X;

  return (
    <Flex align="center" gap="2">
      <IconComponent size="16px" color={color} />
      <Text m="0" fontSize="14px" lineHeight="20px" color="black">
        {item.text}
      </Text>
    </Flex>
  );
};

const InvestmentOptionCard = ({
  option,
  isSelected,
  isExpanded,
  onClick,
}: {
  option: InvestmentOption;
  isSelected: boolean;
  isExpanded: boolean;
  onClick: () => void;
}) => {
  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      w="full"
      bg={isSelected ? "#F4EDFF" : "white"}
      border="1px solid"
      borderColor={isSelected ? "#805AD5" : "#E2E8F0"}
      borderRadius="16px"
      p="4"
      textAlign="left"
      boxShadow="0px 1px 2px rgba(0, 0, 0, 0.12)"
      transition="all 0.15s">
      <Flex align="center" justify="space-between">
        <Box flex="1">
          <Flex align="center" gap="2" mb="2">
            <Text m="0" fontSize="16px" lineHeight="19.2px" fontWeight="700">
              {option.name}
            </Text>

            {option.badge && (
              <Flex
                align="center"
                justify="center"
                h="24px"
                px="3"
                borderRadius="full"
                bg={option.badgeColor ?? "#2F55D4"}>
                <Text m="0" color="white" fontSize="11px" lineHeight="14px" fontWeight="700">
                  {option.badge}
                </Text>
              </Flex>
            )}
          </Flex>

          <Flex align="center" gap="2">
            {getOptionIcon(option)}
            <Text m="0" fontSize="14px" lineHeight="20px" color="#657797">
              {option.label}
            </Text>
          </Flex>
        </Box>

        <Box color="black" ml="3" flexShrink={0}>
          {isExpanded ? <Icon.ChevronUp size="20px" /> : <Icon.ChevronDown size="20px" />}
        </Box>
      </Flex>

      {isExpanded && option.capabilities && (
        <VStack align="stretch" spacing="2" mt="4" pt="4" borderTop="1px solid" borderColor="gray.200">
          {option.capabilities.map((item, index) => (
            <CapabilityRow key={index} item={item} />
          ))}
        </VStack>
      )}
    </Box>
  );
};

const getSavedProfileDisplayName = (profile: SavedProfile) => {
  if (profile.company) return profile.company;
  const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(" ");
  return fullName || "Saved Profile";
};

const getSavedProfileLabel = (type?: SavedProfileType) => {
  const labels: Record<SavedProfileType, string> = {
    INDIVIDUAL: "Individual Account",
    JOINT: "Joint Account",
    TRUST: "Trust Account",
    ENTITY: "Entity Account",
  };
  return type ? labels[type] : "Investment Account";
};

const getVaultLabel = (type?: string) => {
  const labels: Record<string, string> = {
    INDIVIDUAL: "Individual Account",
    JOINT: "Joint Account",
    TRUST: "Trust Account",
    ENTITY: "Entity Account",
  };
  return type ? (labels[type] ?? "Investment Account") : "Investment Account";
};

const getOptionIcon = (option: InvestmentOption) => {
  if (option.type === "scratch") return null;

  const type = option.savedProfile?.ta_account_type ?? option.vault?.ta_account_type;
  if (type === "JOINT") return <Icon.Users size="20px" color="#657797" />;
  if (type === "TRUST") return <Icon.FileText size="20px" color="#657797" />;
  if (type === "ENTITY") return <Icon.Briefcase size="20px" color="#657797" />;
  return <Icon.User size="20px" color="#657797" />;
};

export default WorkflowReinvest;
